import axios from 'axios';
import Cookies from 'universal-cookie';

import { Sentry } from '@config';
import {
  APP_DOMAIN,
  CSRF_COOKIE_NAME,
  CUSTOMER_TYPE,
  PAYMENT_MODEL,
} from '@config/const';
import { configuredFetchBaseQuery } from '@config/rtkQuery';
import {
  toastFulfilledPromise,
  toastPendingPromise,
  toastRejectedPromise,
} from '@features/promiseHandlers';
import { createSlice } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/dist/query/react';

axios.defaults.withCredentials = true;

const cookies = new Cookies();

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: configuredFetchBaseQuery(),
  endpoints: (builder) => ({
    login: builder.mutation({
      query: (credentials) => ({
        url: 'user/login',
        method: 'POST',
        body: credentials,
      }),
      transformErrorResponse: (response) => {
        if (response.status === 400) {
          response.data = response.data?.non_field_errors[0];
          return response;
        }
        const error = new Error('Authentication error');
        Sentry.captureException(error, {
          extra: {
            response,
            status: response.status,
          },
        });

        return { status: 500, data: 'Something went wrong, unexpected error.' };
      },
    }),
    logout: builder.mutation({
      query: () => ({ url: 'user/logout', method: 'POST' }),
      invalidatesTags: (result, error, { login }) =>
        error ? [] : [{ tag: 'Customer', type: login }],
    }),
    releaseHijack: builder.mutation({
      query: () => ({
        url: 'user/release-hijack',
        method: 'POST',
      }),
      invalidatesTags: (result, error, { login }) =>
        error ? [] : [{ tag: 'Customer', type: login }],
    }),
    getUser: builder.query({
      query: () => ({ url: 'customer/me' }),
      providesTags: (result, error) =>
        error ? [] : [{ tag: 'Customer', type: result.login }],
    }),
    getApiToken: builder.query({
      query: () => ({ url: 'customer/auth-token' }),
    }),
    regenerateApiToken: builder.mutation({
      query: () => ({
        url: `customer/auth-token/regenerate`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { login }) => [
        { tag: 'Customer', type: login },
      ],
    }),
    resetPassword: builder.mutation({
      query: ({ email }) => ({
        url: `user/password-reset`,
        method: 'POST',
        body: { email },
      }),
    }),
  }),
});

const setCookieToken = (token) => {
  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + 14);
  cookies.set(CSRF_COOKIE_NAME, token, {
    path: '/',
    domain: APP_DOMAIN,
    expires: expiryDate,
    secure: true,
  });
};

const initialState = {
  token: cookies.get(CSRF_COOKIE_NAME),
  user: {},
  isAuthenticated: false,
  isLoading: false,
  registered: false,
  errorMessage: null,
  toast: {},
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    bringInitialLoginState: (state) => {
      state.token = initialState.token;
      state.user = initialState.user;
      state.isAuthenticated = initialState.isAuthenticated;
      cookies.remove(CSRF_COOKIE_NAME, { path: '/' });
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(authApi.endpoints.login.matchPending, (state, action) =>
        toastPendingPromise(state, action, 'Logging in...')
      )
      .addMatcher(authApi.endpoints.login.matchFulfilled, (state, action) => {
        state.user = {
          isPrepaid:
            action.payload.user.customer_type === CUSTOMER_TYPE.PREPAID,
          isCapacity:
            action.payload.user.payment_model === PAYMENT_MODEL.CAPACITY,
          ...action.payload.user,
        };
        state.isAuthenticated = true;
        toastFulfilledPromise(state, action, {
          render: 'Successfully logged in.',
        });
      })
      .addMatcher(authApi.endpoints.login.matchRejected, toastRejectedPromise)
      .addMatcher(
        authApi.endpoints.getUser.matchFulfilled,
        (state, { payload }) => {
          state.user = {
            isPrepaid: payload.customer_type === CUSTOMER_TYPE.PREPAID,
            isCapacity: payload.payment_model === PAYMENT_MODEL.CAPACITY,
            ...payload,
          };
          state.isAuthenticated = true;
        }
      )
      .addMatcher(
        authApi.endpoints.regenerateApiToken.matchPending,
        (state, action) =>
          toastPendingPromise(state, action, 'Generating new API token...')
      )
      .addMatcher(
        authApi.endpoints.regenerateApiToken.matchFulfilled,
        (state, action) => {
          const { payload } = action;
          cookies.remove(CSRF_COOKIE_NAME, { path: '/' });
          state.token = payload.token;
          setCookieToken(payload.token);
          toastFulfilledPromise(state, action, {
            render: 'New API token generated.',
          });
        }
      )
      .addMatcher(
        authApi.endpoints.regenerateApiToken.matchRejected,
        toastRejectedPromise
      )
      .addMatcher(authApi.endpoints.logout.matchPending, (state, action) => {
        state.isLoading = true;
        toastPendingPromise(state, action, 'Logging out...');
      })
      .addMatcher(authApi.endpoints.logout.matchFulfilled, (state, action) => {
        state.token = null;
        state.isAuthenticated = false;
        state.isLoading = false;
        cookies.remove(CSRF_COOKIE_NAME, { path: '/' });
        toastFulfilledPromise(state, action, {
          render: action.payload.detail,
        });
      })
      .addMatcher(
        authApi.endpoints.resetPassword.matchPending,
        (state, action) =>
          toastPendingPromise(
            state,
            action,
            'Sending request to reset password...'
          )
      )
      .addMatcher(
        authApi.endpoints.resetPassword.matchFulfilled,
        (state, action) =>
          toastFulfilledPromise(state, action, {
            render: 'Password reset email sent.',
          })
      )
      .addMatcher(
        authApi.endpoints.resetPassword.matchRejected,
        toastRejectedPromise
      );
  },
});
export const { bringInitialLoginState } = authSlice.actions;
export default authSlice.reducer;
